import { CSSProperties, isValidElement, useState } from 'react'
import DataSet from './DataSet'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

const camelCaseToSentenceCase = (str: string): string => {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (match) => match.toUpperCase())
        .trim()
}

const toRenderable = (object: { [key: string]: any }, sentenceCase: boolean = true) =>
    Object.entries(object).reduce(
        (acc, [key, value]) => ({
            ...acc,
            [sentenceCase ? camelCaseToSentenceCase(key) : key]: isValidElement(value) ? value : String(value),
        }),
        {}
    )

/** Keys shown first, in this order, when sortKeys is set; all other keys follow alphabetically. */
const PRIORITY_KEYS = ['type', 'channelId', 'streamId', 'localIp', 'localPort']

/** Deterministic key order: PRIORITY_KEYS first (in listed order), then everything else alphabetically. */
export const compareConfigKeys = (a: string, b: string): number => {
    const ia = PRIORITY_KEYS.indexOf(a)
    const ib = PRIORITY_KEYS.indexOf(b)
    if (ia === -1 && ib === -1) return a.localeCompare(b)
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
}

export interface RecursiveDataSetProps {
    object: { [key: string]: any } | string | number
    style?: CSSProperties
    sentenceCase?: boolean
    depth?: number
    defaultExpanded?: boolean
    /** Render properties in a deterministic order (see compareConfigKeys) instead of object insertion order. */
    sortKeys?: boolean
}

const CollapsibleSection = ({
    title,
    children,
    depth = 0,
    defaultExpanded = false,
}: {
    title: string
    children: React.ReactNode
    depth?: number
    defaultExpanded?: boolean
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded)

    return (
        <Box sx={{ mt: depth === 0 ? 2 : 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        borderRadius: 1,
                    },
                    p: 0.5,
                    ml: -0.5,
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <IconButton
                    size="small"
                    sx={{
                        padding: '2px',
                        marginRight: '4px',
                    }}
                >
                    {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </IconButton>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {title}
                </Typography>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ ml: '26px' }}>{children}</Box>
            </Collapse>
        </Box>
    )
}

export const RecursiveDataSet = ({
    object,
    style,
    sentenceCase = false,
    depth = 0,
    defaultExpanded = false,
    sortKeys = false,
}: RecursiveDataSetProps) => {
    if (typeof object !== 'object' || object === null || isValidElement(object)) {
        return (
            <div style={style}>
                <DataSet values={{ '': isValidElement(object) ? object : String(object) }} />
            </div>
        )
    }

    const entries = Object.entries(object)
    if (sortKeys) {
        entries.sort(([a], [b]) => compareConfigKeys(a, b))
    }

    const { flat, objects, arrays } = entries.reduce<{
        flat: { [key: string]: any }
        objects: Array<[string, any]>
        arrays: Array<[string, Array<any>]>
    }>(
        (acc, [key, value]) => {
            if (isValidElement(value)) {
                acc.flat[key] = value
            } else if (Array.isArray(value)) {
                acc.arrays.push([key, value])
            } else if (value && typeof value === 'object') {
                acc.objects.push([key, value])
            } else {
                acc.flat[key] = value
            }
            return acc
        },
        { flat: {}, objects: [], arrays: [] }
    )

    return (
        <div style={style}>
            {Object.keys(flat).length > 0 && <DataSet values={toRenderable(flat, sentenceCase)} />}

            {objects.map(([key, value], ind) => (
                <CollapsibleSection
                    key={`${key}-${ind}`}
                    title={sentenceCase ? camelCaseToSentenceCase(key) : key}
                    depth={depth}
                    defaultExpanded={defaultExpanded}
                >
                    <RecursiveDataSet
                        object={value}
                        sentenceCase={sentenceCase}
                        depth={depth + 1}
                        defaultExpanded={defaultExpanded}
                        sortKeys={sortKeys}
                    />
                </CollapsibleSection>
            ))}

            {arrays.map(([key, value], ind) => (
                <CollapsibleSection
                    key={`${key}-${ind}`}
                    title={sentenceCase ? camelCaseToSentenceCase(key) : key}
                    depth={depth}
                    defaultExpanded={defaultExpanded}
                >
                    {value.map((item, i) => (
                        <CollapsibleSection
                            key={`${key}-item-${i}`}
                            title={`Item ${i + 1}`}
                            depth={depth + 1}
                            defaultExpanded={defaultExpanded}
                        >
                            <RecursiveDataSet
                                object={item}
                                sentenceCase={sentenceCase}
                                depth={depth + 2}
                                defaultExpanded={defaultExpanded}
                                sortKeys={sortKeys}
                            />
                        </CollapsibleSection>
                    ))}
                </CollapsibleSection>
            ))}
        </div>
    )
}
